import { AVATAR_PRESETS } from "@ui/assets/presets";
import { ImagePicker, type Selection } from "@ui/components/ImagePicker";
import type { ReactNode } from "react";

export type { Selection };

/** A preset tile passed through to the underlying ImagePicker. */
export type AvatarPickerPreset = { key: string; alt: string; src?: string; content?: ReactNode };

export interface AvatarPickerProps {
  /** Label text and accessible group name. */
  label: string;
  /** Visually hide the label (the surrounding heading already names it, e.g. in a wizard stage). */
  isLabelHidden?: boolean;
  /** The entity's current/default avatar, rendered as the first selectable tile. Entity-agnostic:
   *  pass a `<PersonAvatar>`, an account initial tile, etc. */
  defaultTile: ReactNode;
  /** Controlled selection state. */
  selection: Selection;
  /** Called when the selection changes. */
  onSelectionChange: (selection: Selection) => void;
  /** Preview URL for the upload slot (blob URL or existing image URL). */
  uploadPreview: string | null;
  /** Called when a file is chosen for upload. */
  onFileSelect: (file: File) => void;
  /** Called when the uploaded file is removed. */
  onRemove: () => void;
  /** Extra preset tiles shown before the shared character presets — e.g. a "use current photo"
   *  option that only applies to one caller. */
  leadingPresets?: readonly AvatarPickerPreset[];
  /** Include the shared blob/star character presets. Default true. */
  includeCharacterPresets?: boolean;
  /** Validation message rendered below the options. */
  errorMessage?: ReactNode;
}

/**
 * Profile-photo picker shared across entities (user, account). Wraps ImagePicker with the avatar
 * conventions — horizontal row layout and the shared character presets — so every "choose a profile
 * picture" affordance reads the same. Display-only: callers own the upload/selection state because
 * each binds to a different form. Size it up by widening the parent container; the row tiles flex to
 * fill it.
 */
export function AvatarPicker({
  label,
  isLabelHidden,
  defaultTile,
  selection,
  onSelectionChange,
  uploadPreview,
  onFileSelect,
  onRemove,
  leadingPresets = [],
  includeCharacterPresets = true,
  errorMessage,
}: AvatarPickerProps) {
  const presetImages = [...leadingPresets, ...(includeCharacterPresets ? AVATAR_PRESETS : [])];

  return (
    <ImagePicker
      defaultOption={defaultTile}
      errorMessage={errorMessage}
      isLabelHidden={isLabelHidden}
      label={label}
      layout="row"
      onFileSelect={onFileSelect}
      onRemove={onRemove}
      onSelectionChange={onSelectionChange}
      presetImages={presetImages}
      selection={selection}
      uploadPreview={uploadPreview}
    />
  );
}
